import axios from "axios";
import { API_URL } from "@/config";
import { useSubscribeForFile } from "@/utils/useSubscribeForFile";

export default {
  namespaced: true,

  state: {
    currentChildBalance: JSON.parse(localStorage.getItem("currentChildBalance")) || {
      amount: 0.0,
      currency: "PLN",
    },
  },

  getters: {
    currentChildBalance: (state) => state.currentChildBalance,
  },

  mutations: {
    setCurrentChildBalance(state, currentChildBalance) {
      localStorage.setItem("currentChildBalance", JSON.stringify(currentChildBalance));

      state.currentChildBalance = currentChildBalance;
    },

    revokeCurrentChildBalance(state) {
      localStorage.removeItem("currentChildBalance");

      state.currentChildBalance = {
        amount: 0.0,
        currency: "PLN",
      };
    },

    // Mirrors revokeCurrentChildBalance - Vuex mutations can't call other mutations,
    // so the localStorage cleanup has to be repeated here in sync with it.
    resetState(state) {
      localStorage.removeItem("currentChildBalance");

      state.currentChildBalance = {
        amount: 0.0,
        currency: "PLN",
      };
    },
  },

  actions: {
    unsetCurrentChildBalance({ commit }) {
      commit("revokeCurrentChildBalance");
    },

    async updateCurrentChildBalance({ commit }) {
      const lastSelectedChildId = localStorage.getItem("lastSelectedChildId");

      if (!lastSelectedChildId) {
        commit("revokeCurrentChildBalance");

        return;
      }

      const currentChildBalance = await axios.get(
        `${API_URL}/children/${lastSelectedChildId}/balance`,
      );

      commit("setCurrentChildBalance", currentChildBalance.data);
    },

    async getMany(_, criteria = {}) {
      const response = await axios.get(`${API_URL}/children`, { params: criteria });

      if (!response.data || typeof response.data.items !== "object") {
        throw new Error("Error occurred while processing your request.");
      }

      return response.data;
    },

    async getOne(_, childId) {
      const response = await axios.get(`${API_URL}/children/${childId}`);

      if (!response.data || typeof response.data.id !== "number") {
        throw new Error("Error occurred while processing your request.");
      }

      return response.data;
    },

    async create(_, childData) {
      const response = await axios.post(`${API_URL}/children`, childData);

      if (response.status !== 201 || !response.data || typeof response.data.id !== "number") {
        throw new Error("Error occurred while processing your request.");
      }

      return response.data;
    },

    async update(_, { childId, childData }) {
      const response = await axios.put(`${API_URL}/children/${childId}`, childData);

      if (response.status !== 200 || !response.data || typeof response.data.id !== "number") {
        throw new Error("Error occurred while processing your request.");
      }

      return response.data;
    },

    async delete(_, childId) {
      const response = await axios.delete(`${API_URL}/children/${childId}`);

      if (response.status !== 200 || !response.data.status) {
        throw new Error("Error occurred while processing your request.");
      }

      return true;
    },

    async activate(_, childId) {
      const response = await axios.post(`${API_URL}/children/${childId}/activate`);

      if (response.status !== 200 || !response.data.status) {
        throw new Error("Error occurred while processing your request.");
      }

      return true;
    },

    async deactivate(_, childId) {
      const response = await axios.post(`${API_URL}/children/${childId}/deactivate`);

      if (response.status !== 200 || !response.data.status) {
        throw new Error("Error occurred while processing your request.");
      }

      return true;
    },

    async archive(_, childId) {
      const res = await axios.post(`${API_URL}/children/${childId}/archive`);

      if (res.status !== 200) {
        throw new Error("Error occurred while processing your request.");
      }

      return true;
    },

    async unarchive(_, childId) {
      const res = await axios.post(`${API_URL}/children/${childId}/unarchive`);

      if (res.status !== 200) {
        throw new Error("Error occurred while processing your request.");
      }

      return true;
    },

    async export({ rootGetters, commit }, criteria) {
      const { subscribeForFile } = useSubscribeForFile(rootGetters, commit, true);

      await subscribeForFile("children/export", {
        url: `${API_URL}/children/export`,
        method: "POST",
        data: { ...criteria },
      });
    },

    async import(_, formData) {
      const response = await axios.post(`${API_URL}/children/import`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.status !== 200 || !response.data.status) {
        throw new Error("Error occurred while processing your request.");
      }

      return response.data;
    },

    async getChildrenOptions(_, criteria = {}) {
      const res = await axios.get(`${API_URL}/children/options`, { params: criteria });

      if (!res.data || typeof res.data !== "object") {
        throw new Error("Error occurred while processing your request.");
      }

      return res.data;
    },

    async changeClassroom(_, { childId, classroomId }) {
      const response = await axios.put(
        `${API_URL}/children/${childId}/changeClassroom/${classroomId}`,
      );

      if (response.status !== 200) {
        throw new Error("Error occurred while processing your request.");
      }

      return response.data;
    },

    async changeCardNumber(_, { childId, cardNumber }) {
      const response = await axios.put(`${API_URL}/children/${childId}/changeCardNumber`, {
        cardNumber,
      });

      if (response.status !== 200) {
        throw new Error("Error occurred while processing your request.");
      }

      return response.data;
    },

    async bulkDeactivate(_, childrenIds) {
      const response = await axios.post(`${API_URL}/childrenDeactivation`, { childrenIds });

      if (response.status !== 200) {
        throw new Error("Error occurred while processing your request.");
      }

      return true;
    },
  },
};
